import { useEffect, useMemo, useState } from "react"
import MultiSelectWithChips, {
  TMultiSelectWithChipsOption,
  TMultiSelectWithChipsOptionGroup,
} from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import { Loader } from "~/ui-rtk/components/ui/common"
import { useAppSession } from "~/ui-rtk/pages/Auth/useCubeApiToken"
import {
  metricEntryFor,
  useMetricDictionary,
} from "~/ui-rtk/hooks/metric-dictionary"
import { cubeOf } from "../utils/derive-report"

export type TMetricSelection = {
  measures: string[]
  dimensions: string[]
  /** Time dimension member for the selected cube (detected from meta), if any. */
  timeDimension?: string
}

type CubeMember = {
  name: string
  title?: string
  shortTitle?: string
  type?: string
}
type CubeMetaCube = {
  name: string
  title?: string
  measures?: CubeMember[]
  dimensions?: CubeMember[]
}

// Internal Cube probe cube — never user-selectable (leaks infra metadata).
const HIDDEN_CUBES = new Set(["db_capabilities"])

/**
 * Detect the time dimension for a selection's (single) cube so saved queries are
 * self-describing: returns md_date when present, else the first `time` dimension,
 * else undefined (cube has no time axis -> no date filtering downstream).
 */
const detectTimeDimension = (
  members: string[],
  cubes: CubeMetaCube[],
): string | undefined => {
  const prefixes = new Set(members.map(cubeOf))
  if (prefixes.size !== 1) return undefined
  const [prefix] = [...prefixes]
  const cube = cubes.find(c => c.name === prefix)
  if (!cube) return undefined
  const timeDims = (cube.dimensions ?? []).filter(d => d.type === "time")
  const md = timeDims.find(d => d.name.endsWith(".md_date"))
  return (md ?? timeDims[0])?.name
}

type Props = {
  value: TMetricSelection
  onChange: (next: TMetricSelection) => void
}

/**
 * "Metrics explorer" used inside the custom-report editor. Enumerates ALL cube
 * members from the live Cube schema (cubeApi.meta()) so any measure/dimension is
 * selectable, enriches labels with Metric Dictionary titles, and detects the
 * cube's time dimension. A section should draw from a SINGLE cube (Cube can only
 * join cubes that declare joins); the picker warns when that is violated.
 */
export default function MetricExplorerPicker({ value, onChange }: Props) {
  const { cubeApi } = useAppSession()
  const { metrics: dictMetrics, metricMap } = useMetricDictionary()
  const [cubes, setCubes] = useState<CubeMetaCube[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    cubeApi
      .meta()
      .then(meta => {
        if (cancelled) return
        const list = (meta as unknown as { cubes?: CubeMetaCube[] }).cubes ?? []
        setCubes(list.filter(c => !HIDDEN_CUBES.has(c.name)))
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load the metric catalog.")
      })
    return () => {
      cancelled = true
    }
  }, [cubeApi])

  // Friendly label: Metric Dictionary title -> cube title -> raw member.
  // Used only by the cube-meta fallback below.
  const labelFor = (member: CubeMember): string => {
    const dict = metricEntryFor(metricMap, member.name)
    return dict?.title || member.title || member.shortTitle || member.name
  }

  // Every measure member queryable for this tenant (`cube.measure`). The
  // dictionary is the curated catalog; this live set gates it to metrics that
  // actually exist here — which also drops synthetic dictionary keys
  // (computed.*, initiative.*) that aren't real cube measures.
  const availableMeasures = useMemo(() => {
    const set = new Set<string>()
    for (const cube of cubes ?? []) {
      for (const measure of cube.measures ?? []) {
        set.add(measure.name)
      }
    }
    return set
  }, [cubes])

  // Dictionary key -> queryable cube member: bare keys belong to the `metrics`
  // cube; dotted keys are already fully qualified.
  const memberOf = (key: string) => (key.includes(".") ? key : `metrics.${key}`)

  const { measureOptions, groups } = useMemo(() => {
    const mOpts: TMultiSelectWithChipsOption[] = []
    const groupLabels = new Set<string>()

    for (const metric of dictMetrics) {
      const member = memberOf(metric.key)
      if (!availableMeasures.has(member)) continue
      const group = metric.category[0] ?? "Other"
      groupLabels.add(group)
      mOpts.push({ label: metric.title, value: member, group })
    }

    // Fallback: if the dictionary is empty/unavailable, fall back to raw cube
    // measures so the picker never becomes unusable (grouped by cube).
    if (mOpts.length === 0 && (cubes?.length ?? 0) > 0) {
      const groupSet: TMultiSelectWithChipsOptionGroup[] = []
      for (const cube of cubes ?? []) {
        groupSet.push({ label: cube.title || cube.name, value: cube.name })
        for (const measure of cube.measures ?? []) {
          mOpts.push({
            label: labelFor(measure),
            value: measure.name,
            group: cube.name,
          })
        }
      }
      return { measureOptions: mOpts, groups: groupSet }
    }

    // Non-destructive: keep any already-selected measure that isn't in the
    // dictionary (e.g. a legacy section built from a non-dictionary cube) so
    // editing a report never silently drops it.
    const present = new Set(mOpts.map(o => o.value))
    const orphanGroup = "Current selection"
    let hasOrphan = false
    for (const member of value.measures) {
      if (present.has(member)) continue
      hasOrphan = true
      const dict = metricEntryFor(metricMap, member)
      mOpts.push({
        label: dict?.title ?? member,
        value: member,
        group: orphanGroup,
      })
    }

    const groupSet: TMultiSelectWithChipsOptionGroup[] = [...groupLabels]
      .sort()
      .map(g => ({ label: g, value: g }))
    if (hasOrphan) groupSet.push({ label: orphanGroup, value: orphanGroup })
    return { measureOptions: mOpts, groups: groupSet }
  }, [dictMetrics, metricMap, availableMeasures, cubes, value.measures])

  const selectedMeasureOptions = useMemo(
    () => measureOptions.filter(o => value.measures.includes(o.value)),
    [measureOptions, value.measures],
  )

  const selectedCubeCount = useMemo(
    () => new Set(value.measures.map(cubeOf)).size,
    [value.measures],
  )

  // Group-by was removed: sections are measures-only. Always emit an empty
  // `dimensions` array so saved queries aggregate to period totals, and detect
  // the time dimension from the selected measures alone.
  const emit = (measures: string[]) =>
    onChange({
      measures,
      dimensions: [],
      timeDimension: detectTimeDimension(measures, cubes ?? []),
    })

  if (error) {
    return <div className="text-error text-sm py-4">{error}</div>
  }
  if (!cubes) {
    return (
      <div className="flex items-center gap-2 text-system-gray-400 py-4">
        <Loader size={20} /> Loading metric catalog…
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-system-sm">
      <div>
        <label className="text-sm text-system-gray-300 mb-1 block">
          Measures
        </label>
        <MultiSelectWithChips
          label="Measures"
          options={measureOptions}
          groups={groups}
          showTypeAhead
          value={selectedMeasureOptions}
          onChange={opts => emit(opts.map(o => o.value))}
        />
      </div>
      {selectedCubeCount > 1 && (
        <div className="text-warning text-sm">
          Pick measures from a single cube only — cross-cube sections can&apos;t
          be queried.
        </div>
      )}
    </div>
  )
}
